import TrackedEvent from '@/Enums/Creatives/TrackedEvent.enum';
import { type RefObject, useCallback, useEffect, useState } from 'react';

export function useVastPlaybackTracking(mediaRef: RefObject<HTMLMediaElement | null>) {
  const [firedEvents, setFiredEvents] = useState<Set<TrackedEvent>>(new Set());
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  const fireEvent = useCallback((...events: TrackedEvent[]) => {
    setFiredEvents(prev => {
      const next = new Set(prev);
      for (const e of events) next.add(e);
      if (next.size === prev.size) return prev;
      return next;
    });
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const el = mediaRef.current;
    if (!el || !el.duration || Number.isNaN(el.duration)) return;

    const progress = el.currentTime / el.duration;
    setRemainingSeconds(Math.ceil(el.duration - el.currentTime));

    const toFire: TrackedEvent[] = [TrackedEvent.IMPRESSION, TrackedEvent.PROGRESS];

    if (progress >= 0.25) toFire.push(TrackedEvent.FIRST_QUARTILE);
    if (progress >= 0.5) toFire.push(TrackedEvent.MIDPOINT);
    if (progress >= 0.75) toFire.push(TrackedEvent.THIRD_QUARTILE);

    fireEvent(...toFire);
  }, [mediaRef, fireEvent]);

  const handleEnded = useCallback(() => {
    fireEvent(
      TrackedEvent.IMPRESSION,
      TrackedEvent.FIRST_QUARTILE,
      TrackedEvent.MIDPOINT,
      TrackedEvent.THIRD_QUARTILE,
      TrackedEvent.COMPLETE,
    );
  }, [fireEvent]);

  const handleError = useCallback(() => {
    fireEvent(TrackedEvent.ERROR);
  }, [fireEvent]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;

    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('ended', handleEnded);
    el.addEventListener('error', handleError);

    return () => {
      el.removeEventListener('timeupdate', handleTimeUpdate);
      el.removeEventListener('ended', handleEnded);
      el.removeEventListener('error', handleError);
    };
  }, [mediaRef, handleTimeUpdate, handleEnded, handleError]);

  return { firedEvents, fireEvent, remainingSeconds };
}
